import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  OctagonAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../lib/cn";

export type ToastTone = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss. 0 keeps it until dismissed. */
  duration: number;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id" | "duration"> & { duration?: number }) => string;
  dismiss: (id: string) => void;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toneStyles: Record<ToastTone, { icon: LucideIcon; accent: string; iconClass: string }> = {
  success: {
    icon: CheckCircle2,
    accent: "bg-success-500",
    iconClass: "text-success-500",
  },
  error: { icon: OctagonAlert, accent: "bg-danger-500", iconClass: "text-danger-500" },
  warning: {
    icon: AlertTriangle,
    accent: "bg-warning-500",
    iconClass: "text-warning-500",
  },
  info: { icon: Info, accent: "bg-gold-500", iconClass: "text-gold-500" },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback<ToastContextValue["push"]>(
    ({ duration = 5_000, ...rest }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      // Errors stay put — losing the only explanation of a failure after five
      // seconds is how people end up not reporting bugs.
      const resolvedDuration = rest.tone === "error" ? Math.max(duration, 9_000) : duration;

      setToasts((current) => [...current.slice(-3), { id, duration: resolvedDuration, ...rest }]);

      if (resolvedDuration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), resolvedDuration),
        );
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      push,
      dismiss,
      success: (title, description) => push({ tone: "success", title, description }),
      error: (title, description) => push({ tone: "error", title, description }),
      warning: (title, description) => push({ tone: "warning", title, description }),
      info: (title, description) => push({ tone: "info", title, description }),
    }),
    [toasts, push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside a <ToastProvider>");
  }
  return context;
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:top-0 sm:bottom-auto sm:items-end"
    >
      <AnimatePresence initial={false}>
        {toasts.map((toast) => {
          const { icon: Icon, accent, iconClass } = toneStyles[toast.tone];
          return (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 24, scale: 0.96 }}
              transition={{ type: "spring", stiffness: 420, damping: 34 }}
              role={toast.tone === "error" ? "alert" : "status"}
              className="pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-xl border border-line bg-surface shadow-ir-lg"
            >
              <span aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-1", accent)} />
              <div className="flex items-start gap-3 py-3 pl-4 pr-2">
                <Icon size={17} aria-hidden="true" className={cn("mt-0.5 shrink-0", iconClass)} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-content">{toast.title}</p>
                  {toast.description && (
                    <p className="mt-0.5 text-xs text-content-muted">{toast.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onDismiss(toast.id)}
                  aria-label="Dismiss notification"
                  className="grid size-7 shrink-0 place-items-center rounded-md text-content-subtle transition-colors hover:bg-surface-sunken hover:text-content"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
